import React from 'react';
import CloseIcon from '@mui/icons-material/Close';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import { Rating } from '@mui/material';
import {
  StyledAdminBusinessEditDialog,
  AdminBusinessEditModalTitle,
  AdminBusinessEditModalCloseButton,
  AdminBusinessEditModalContent,
  AdminBusinessEditModalActions,
  AdminBusinessEditCancelButton,
  AdminBusinessEditSaveButton,
  AdminBusinessEditSection,
  AdminBusinessEditSectionTitle,
  AdminBusinessEditSectionDescription,
  AdminBusinessEditField,
  AdminBusinessEditHint,
  AdminBusinessEditRatingRow,
} from './AdminBusinessEditModal.styled';

/**
 * Modal for editing per-business admin settings. Add new sections here as fields grow.
 */
function AdminBusinessEditModal({
  open,
  business,
  formValues,
  onFieldChange,
  onSave,
  onClose,
  saving = false,
  saveDisabled = false,
}) {
  const businessName = business?.BusinessName || 'Business';
  const savedPlaceId = String(business?.GoogleReview?.PlaceId ?? '').trim();
  const googleRating = business?.GoogleReview?.Rating;
  const googleReviewCount = Number(business?.GoogleReview?.ReviewCount ?? 0);
  const hasGoogleRatingSummary =
    googleRating != null &&
    !Number.isNaN(Number(googleRating)) &&
    googleReviewCount > 0;

  return (
    <StyledAdminBusinessEditDialog
      open={open}
      onClose={saving ? undefined : onClose}
      aria-labelledby="admin-business-edit-dialog-title"
      maxWidth="sm"
      fullWidth
    >
      <AdminBusinessEditModalTitle id="admin-business-edit-dialog-title">
        <span className="admin-business-edit-icon-wrap" aria-hidden>
          <StorefrontOutlinedIcon className="admin-business-edit-icon" />
        </span>
        {businessName}
        <AdminBusinessEditModalCloseButton
          aria-label="Close"
          onClick={onClose}
          disabled={saving}
          size="small"
        >
          <CloseIcon fontSize="small" />
        </AdminBusinessEditModalCloseButton>
      </AdminBusinessEditModalTitle>

      <AdminBusinessEditModalContent>
        <AdminBusinessEditSection>
          <AdminBusinessEditSectionTitle component="h3">
            Google reviews
          </AdminBusinessEditSectionTitle>
          <AdminBusinessEditSectionDescription>
            Link this storefront to a Google Business Profile so ratings can display with
            Google attribution.
          </AdminBusinessEditSectionDescription>
          <AdminBusinessEditField
            fullWidth
            size="small"
            label="Google Place ID"
            placeholder="ChIJ…"
            value={formValues.googlePlaceId}
            disabled={saving}
            onChange={(e) => onFieldChange('googlePlaceId', e.target.value)}
            inputProps={{
              'aria-label': `Google Place ID for ${businessName}`,
            }}
          />
          {hasGoogleRatingSummary ? (
            <AdminBusinessEditRatingRow>
              <Rating
                value={Number(googleRating)}
                precision={0.1}
                readOnly
                size="small"
                sx={{ color: '#fbbc04' }}
              />
              <AdminBusinessEditHint component="span">
                {Number(googleRating).toFixed(1)} ·{' '}
                {googleReviewCount.toLocaleString()} Google review
                {googleReviewCount === 1 ? '' : 's'}
              </AdminBusinessEditHint>
            </AdminBusinessEditRatingRow>
          ) : (
            <AdminBusinessEditHint>
              {savedPlaceId
                ? 'Place ID saved — rating loads on next storefront view.'
                : 'Not linked — paste a Place ID from Google Maps.'}
            </AdminBusinessEditHint>
          )}
        </AdminBusinessEditSection>
      </AdminBusinessEditModalContent>

      <AdminBusinessEditModalActions>
        <AdminBusinessEditCancelButton
          variant="outlined"
          color="inherit"
          onClick={onClose}
          disabled={saving}
        >
          Cancel
        </AdminBusinessEditCancelButton>
        <AdminBusinessEditSaveButton
          variant="contained"
          disableElevation
          onClick={onSave}
          disabled={saving || saveDisabled}
        >
          {saving ? 'Saving…' : 'Save changes'}
        </AdminBusinessEditSaveButton>
      </AdminBusinessEditModalActions>
    </StyledAdminBusinessEditDialog>
  );
}

export default AdminBusinessEditModal;
